import { constructAsync } from '../../../webmodule-common/other/async-constructor';
import { customElement, state } from 'lit/decorators.js';
import { DataBinding } from '../../../webmodule-common/other/ui/databinding/databinding';
import { DataEntryOwner } from '../../../webmodule-common/other/ui/DataEntryOwner';
import { DataEntryPageControlView, ModalViewBase } from '../../../webmodule-common/other/ui/data-entry-screen-base';
import { DataTracker, FieldType } from '../../../webmodule-common/other/ui/databinding/data-tracker';
import { FabricationJobContainerManager } from '../data/fabrication-job-container';
import { FabricationJobItemsTable, JobItemRow } from './fabrication-job-items-table';
import { FabricationJobItemFrameView } from './fabrication-job-item-frame-view';
import { FormInputAssistant } from '../../../webmodule-common/other/ui/templateresult/form-input-assistant';
import { getApiFactory } from '../../api/api-injector';
import { getFabricationJobStatus } from '../data/fabrication-job-helper-functions';
import { getQuoteContainerManager } from '../../dealer-franchisee/quotes/quote-ui-adapter';
import { html, TemplateResult } from 'lit';
import { LangFabricationJobDetailView } from '../../../localization/scripts/fabrication-jobs/views/fabrication-job-detail-view-lang';
import { PageControl, PageControlOptions, PageManager } from '../../../webmodule-common/other/ui/page-control';
import { QuoteContainerManager } from '../../quotes/data/quote-container';
import { QuoteItemView, QuoteItemViewOptions } from '../../quotes/views/quote-item-view';
import { Snippet } from '../../../webmodule-common/other/ui/events';
import { supplierQuoteItemContentType } from '../../quotes/data/supplier-quote-item-content-type';

export interface FabricationJobViewOptions {
  containerManager: FabricationJobContainerManager;
  onDelete?: () => Promise<void>;
}

@customElement('wm-fabricationjobdataentryview')
export class FabricationJobDataEntryView extends DataEntryPageControlView {
  private containerManager: FabricationJobContainerManager;
  private dataBinding: DataBinding;
  private dataTracker: DataTracker;
  private onDelete?: () => Promise<void>;
  private quoteManagerCache = new Map<string, QuoteContainerManager>();
  @state()
  private itemsTable?: FabricationJobItemsTable;

  constructor(options: FabricationJobViewOptions, owner: DataEntryOwner) {
    super(owner);
    this.containerManager = options.containerManager;
    this.onDelete = options.onDelete;

    this.dataBinding = new DataBinding(this.ui, undefined, input => `fabrication-job-${input}`);
    this.dataTracker = new DataTracker(this.dataBinding);

    const cm = this.containerManager;
    const addField = (fieldName: string, type: FieldType = FieldType.string) => {
      this.dataTracker.addObjectBinding(() => cm.fabricationJob, fieldName, fieldName, type, false);
    };

    addField('title');
    addField('description');
    addField('dateCreated', FieldType.dateTime);
    addField('lastModifiedDate', FieldType.dateTime);
  }

  override disconnectedCallback(): void {
    super.disconnectedCallback();
    this.quoteManagerCache.clear();
  }

  getTitle(): Snippet {
    const job = this.containerManager.fabricationJob;
    return html`${LangFabricationJobDetailView.jobTitleHeading(
      job.jobNumber.toString(),
      job.title || LangFabricationJobDetailView.untitled()
    )}
    ${getFabricationJobStatus(job.state, true)}`;
  }

  public isDataReadonly(): boolean {
    return this.containerManager.isReadOnly();
  }

  public internalDataChanged(): boolean {
    return !this.abandoned && this.containerManager.jobChanged();
  }

  public async prepareForSave(): Promise<void> {
    if (this.isDataReadonly()) return;
    if (this.dataTracker.modified) this.dataTracker.applyChangeToValue();
  }

  protected async internalSaveData(): Promise<boolean> {
    const job = this.containerManager.fabricationJob;
    const result = await this.containerManager.updateJob(job.title, job.description);
    return result !== null;
  }

  async setActiveTabByHash(): Promise<void> {
    // Handle quote-item-{quoteItemId} hash — navigate to Job Items tab and open the item
    const hash = window.location.hash.replace('#', '');
    if (hash.startsWith('quote-item-')) {
      const quoteItemId = hash.substring('quote-item-'.length);
      const jobItem = this.containerManager.items.find(i => i.quoteItemId === quoteItemId);
      if (jobItem) {
        // Switch to items tab first
        const itemsPage = this.pageControl.pages.find(p => p.pageFragment === 'items');
        if (itemsPage) await this.pageControl.setActivePage(itemsPage);
        // Open the item tab using a minimal row — openQuoteItemTab loads quote data itself
        await this.openQuoteItemTab({ fabricationJobItem: jobItem });
        return;
      }
    }
    await super.setActiveTabByHash();
  }

  protected createPageControl(): PageControl {
    const options: PageControlOptions = {
      defaultTabIndex: 0,
      menuIcons: [],
      pageInitializer: () => [this.createInformationTab(), this.createJobItemsTab()]
    };
    return new PageControl(options);
  }

  private createInformationTab(): PageManager {
    return {
      caption: () => LangFabricationJobDetailView.informationTab(),
      canClose: () => Promise.resolve(false),
      canLeave: async () => await this.allowPageSwitch(),
      hasDelete: () => false,
      onEnter: async () => {},
      content: (_pageIndex: number) => this.informationTemplate(),
      data: undefined,
      pageFragment: 'information'
    };
  }

  private createJobItemsTab(): PageManager {
    return {
      caption: () => `${LangFabricationJobDetailView.jobItemsTab()} (${this.containerManager.items.length})`,
      canClose: () => Promise.resolve(false),
      canLeave: async () => await this.allowPageSwitch(),
      hasDelete: () => false,
      onEnter: async () => {
        if (!this.itemsTable) {
          this.itemsTable = await constructAsync(
            new FabricationJobItemsTable({
              containerManager: this.containerManager,
              isReadonly: () => this.isDataReadonly(),
              onItemClick: async (row: JobItemRow) => this.openQuoteItemTab(row)
            })
          );
        } else {
          // Re-render the table to pick up any in-memory changes (e.g. unitOverrides
          // updated after editing a job item's V6 options).
          this.itemsTable.refreshData();
        }
      },
      content: (_pageIndex: number) => {
        return html` ${this.itemsTable?.ui ?? html`<div>${LangFabricationJobDetailView.loadingText()}</div>`} `;
      },
      buttonMenu: () => {
        return html`
          <webmodule-button size="small" @click=${() => this.refreshJobItems()} variant="default">
            ${LangFabricationJobDetailView.refreshButton()}
          </webmodule-button>
        `;
      },
      data: undefined,
      pageFragment: 'items'
    };
  }

  public async refreshItemsTable(): Promise<void> {
    if (this.itemsTable) {
      await this.itemsTable.refreshData();
    }
    this.requestUpdate();
  }

  private async refreshJobItems(): Promise<void> {
    await this.containerManager.loadFromServer();
    await this.refreshItemsTable();
  }

  private informationTemplate(): TemplateResult {
    const readonly = this.isDataReadonly();
    const forms = new FormInputAssistant(this.dataTracker, readonly);

    return html`
      <form class="py-3 px-0 form-two-col">
        <div class="row">
          <div>
            ${forms.text('title', LangFabricationJobDetailView.titleLabel(), 200)}
            ${forms.note('description', LangFabricationJobDetailView.descriptionLabel(), 3000)}
            ${forms.dateReadonly('dateCreated', LangFabricationJobDetailView.createdLabel())}
            ${forms.dateReadonly('lastModifiedDate', LangFabricationJobDetailView.modifiedLabel())}
          </div>
        </div>
      </form>
    `;
  }

  private async openQuoteItemTab(row: JobItemRow): Promise<void> {
    // Focus existing tab if already open
    const fabItemId = row.fabricationJobItem.id;
    const existingPage = this.pageControl.pages.find(p => p.pageFragment === `fab-item-${fabItemId}`);
    if (existingPage) {
      await this.pageControl.setActivePage(existingPage);
      return;
    }

    // Resolve quote item — may come pre-loaded from the items table, or we need to look it up
    let qi = row.quoteItem;
    if (!qi) {
      // Look up the quote item via the quote numbers endpoint to get the quoteId,
      // then load the quote to find the item
      const quoteNumberResult = await getApiFactory()
        .quote()
        .getQuoteNumbersByQuoteItemIds({
          quoteItemIds: [row.fabricationJobItem.quoteItemId]
        });
      const mapping = quoteNumberResult?.items?.[0];
      if (!mapping) return;
      const qm = getQuoteContainerManager(mapping.quoteId);
      qm.forceLockout = true;
      await qm.needsQuote();
      await qm.needsQuoteItems();
      this.quoteManagerCache.set(mapping.quoteId, qm);
      qi = qm.quoteItemContainer(row.fabricationJobItem.quoteItemId)?.item;
      if (!qi) return;
    }

    // Load/cache QuoteContainerManager for this quote
    const quoteId = qi.quoteId;
    let qm = this.quoteManagerCache.get(quoteId);
    if (!qm) {
      qm = getQuoteContainerManager(quoteId);
      qm.forceLockout = true;
      await qm.needsQuote();
      await qm.needsQuoteItems();
      this.quoteManagerCache.set(quoteId, qm);
    }

    const container = qm.quoteItemContainer(qi.id);
    const quoteItemViewOptions: QuoteItemViewOptions = {
      supplierId: qm.quote.supplierId,
      quoteManager: qm,
      quoteItemContainer: container,
      isClientFacing: () => false
    };

    // Create the V6 frame view with overrides, or a standard view for non-frame items
    let quoteItemView: QuoteItemView | null = null;
    const providerType = container.item.serviceProvider;
    const contentType = container.item.quoteItemContentType;
    if (providerType !== '' && qm.isV6 && contentType === supplierQuoteItemContentType.CID_FRAM) {
      quoteItemView = await constructAsync(
        new FabricationJobItemFrameView({
          quoteItemViewOptions,
          fabricationJobItem: row.fabricationJobItem,
          fabricationJobManager: this.containerManager,
          quoteManager: qm,
          quoteItemContainer: container,
          supplierId: qm.quote.supplierId
        })
      );
    } else {
      quoteItemView = await constructAsync(new QuoteItemView(quoteItemViewOptions));
    }
    if (!quoteItemView) return;

    try {
      await quoteItemView.prepareEditor();
      if (!quoteItemView.readyToEdit) return;
    } catch {
      return;
    }

    if (!quoteItemView.isTab()) return;
    const page = quoteItemView.createPageManager();
    page.pageFragment = `fab-item-${fabItemId}`;
    this.pageControl.addPage(page);
  }

  private async deleteJob(): Promise<void> {
    if (this.onDelete) {
      await this.onDelete();
    }
  }
}

@customElement('wm-fabricationjobview')
export class FabricationJobView extends ModalViewBase {
  view: FabricationJobDataEntryView | null = null;
  private options: FabricationJobViewOptions;

  constructor(options: FabricationJobViewOptions) {
    super();
    this.options = options;
  }

  protected get modalSize() {
    return 'modal-fullscreen';
  }

  async canClose(): Promise<boolean> {
    return (await this.view?.canClose()) ?? true;
  }

  async afterConstruction(): Promise<void> {
    this.view = await constructAsync(new FabricationJobDataEntryView(this.options, this));
  }

  protected getTitle(): Snippet {
    return this.view?.getTitle() ?? '';
  }

  protected bodyTemplate(): TemplateResult {
    return html`${this.view?.ui}`;
  }
}
